import { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = `${BACKEND_URL}/api`;

const AdminProfilePage = () => {
  const { user, getAuthHeader } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  const createdAtText = useMemo(() => {
    if (!user?.created_at) return '-';
    const date = new Date(user.created_at);
    if (Number.isNaN(date.getTime())) return user.created_at;
    return date.toLocaleString('tr-TR');
  }, [user?.created_at]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword) {
      toast.error('Lütfen mevcut ve yeni şifrenizi girin.');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Yeni şifre en az 6 karakter olmalı.');
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      toast.error('Yeni şifre ve tekrar alanı aynı olmalı.');
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/auth/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        { headers: getAuthHeader() }
      );
      toast.success('Şifreniz başarıyla güncellendi.');
      setCurrentPassword('');
      setNewPassword('');
      setNewPasswordConfirm('');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Şifre güncellenemedi.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F1EA]" data-testid="admin-profile-page">
      <Sidebar />
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-[#2E2620] font-['Playfair_Display']">
              Kişisel Bilgilerim
            </h1>
            <p className="text-[#7A6A5C] mt-2">
              Hesap bilgilerinizi görüntüleyebilir ve şifrenizi değiştirebilirsiniz.
            </p>
          </div>

          <Card className="border-[#E6DCCF]">
            <CardHeader>
              <CardTitle className="text-[#2E2620]">Hesap Bilgileri</CardTitle>
              <CardDescription className="text-[#7A6A5C]">Sadece sizin görebileceğiniz bilgiler</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white border border-[#E6DCCF] rounded-lg p-4">
                <p className="text-xs text-[#A89F91]">Ad Soyad</p>
                <p className="text-[#2E2620] font-medium">{user?.full_name || '-'}</p>
              </div>
              <div className="bg-white border border-[#E6DCCF] rounded-lg p-4">
                <p className="text-xs text-[#A89F91]">E-posta</p>
                <p className="text-[#2E2620] font-medium break-all">{user?.email || '-'}</p>
              </div>
              <div className="bg-white border border-[#E6DCCF] rounded-lg p-4">
                <p className="text-xs text-[#A89F91]">Rol</p>
                <p className="text-[#2E2620] font-medium">{user?.role || '-'}</p>
              </div>
              <div className="bg-white border border-[#E6DCCF] rounded-lg p-4">
                <p className="text-xs text-[#A89F91]">Kayıt Tarihi</p>
                <p className="text-[#2E2620] font-medium">{createdAtText}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#E6DCCF]">
            <CardHeader>
              <CardTitle className="text-[#2E2620]">Şifremi Değiştir</CardTitle>
              <CardDescription className="text-[#7A6A5C]">Güvenliğiniz için düzenli aralıklarla güncelleyin</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="current-password">Mevcut Şifre</Label>
                  <Input
                    id="current-password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Mevcut şifreniz"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password">Yeni Şifre</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="En az 6 karakter"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password-confirm">Yeni Şifre (Tekrar)</Label>
                  <Input
                    id="new-password-confirm"
                    type="password"
                    value={newPasswordConfirm}
                    onChange={(e) => setNewPasswordConfirm(e.target.value)}
                    placeholder="Yeni şifrenizi tekrar girin"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-[#C86F4A] hover:bg-[#B05D3A] text-white rounded-full"
                >
                  {saving ? 'Kaydediliyor...' : 'Şifreyi Güncelle'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminProfilePage;
